import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import { supabase, hashPassword } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';
import LoginModal from '../../../components/feature/LoginModal';

interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  last_login?: string;
  is_active: boolean;
}

// Super admin email - cannot be deleted or demoted
const SUPER_ADMIN_EMAIL = 'admin@ibxshipping.com';

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userForPassword, setUserForPassword] = useState<User | null>(null);
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSuperAdminPasswordModal, setShowSuperAdminPasswordModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'staff',
    password: '',
    is_active: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [superAdminNewPassword, setSuperAdminNewPassword] = useState('');
  const [superAdminPasswordLoading, setSuperAdminPasswordLoading] = useState(false);

  // Check authentication and super admin role
  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    } else if (user.email !== SUPER_ADMIN_EMAIL) {
      alert('Access denied. Only the super administrator can manage users.');
      navigate('/admin');
    } else {
      fetchUsers();
    }
  }, [user, navigate]);

  const handleLogin = (userData: any) => {
    login(userData);
    setShowLoginModal(false);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  const generateNewPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const generateSuperAdminPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setSuperAdminNewPassword(password);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      // Check if email already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('email')
        .eq('email', formData.email)
        .single();

      if (existingUser) {
        setFormError('Email already exists. Please use a different email.');
        return;
      }

      // Hash the password before storing
      const hashedPassword = await hashPassword(formData.password);

      // Create new user
      const { error } = await supabase
        .from('users')
        .insert([{
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          password: hashedPassword,
          is_active: formData.is_active,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      // Reset form and close modal
      const plainPassword = formData.password; // Save for display
      setFormData({
        email: '',
        full_name: '',
        role: 'staff',
        password: '',
        is_active: true
      });
      setShowCreateModal(false);
      fetchUsers();
      
      alert(`User created successfully!\n\nLogin Credentials:\nEmail: ${formData.email}\nPassword: ${plainPassword}\n\nPlease share these credentials securely with the user.`);
    } catch (error) {
      console.error('Error creating user:', error);
      setFormError('Failed to create user. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    // Prevent demoting super admin
    if (selectedUser.email === SUPER_ADMIN_EMAIL && formData.role !== 'admin') {
      setFormError('Cannot change the role of the super administrator.');
      return;
    }

    setFormLoading(true);
    setFormError('');

    try {
      const updateData: any = {
        full_name: formData.full_name,
        role: formData.role,
        is_active: formData.is_active
      };

      // Only update password if provided
      let plainPassword = '';
      if (formData.password) {
        updateData.password = await hashPassword(formData.password);
        plainPassword = formData.password;
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', selectedUser.id);

      if (error) throw error;

      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      
      if (plainPassword) {
        alert(`User updated successfully!\n\nNew Password: ${plainPassword}\n\nPlease share the new password securely with the user.`);
      } else {
        alert('User updated successfully!');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setFormError('Failed to update user. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!userForPassword || !newPassword) return;

    setPasswordLoading(true);

    try {
      // Hash the new password
      const hashedPassword = await hashPassword(newPassword);
      
      const { error } = await supabase
        .from('users')
        .update({ password: hashedPassword })
        .eq('id', userForPassword.id);

      if (error) throw error;

      const plainPassword = newPassword; // Save for display
      setShowPasswordModal(false);
      setUserForPassword(null);
      setNewPassword('');
      
      alert(`Password changed successfully!\n\nNew Password: ${plainPassword}\n\nPlease share the new password securely with ${userForPassword.full_name}.`);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeSuperAdminPassword = async () => {
    if (!superAdminNewPassword || !user) return;

    setSuperAdminPasswordLoading(true);

    try {
      // Hash the new password
      const hashedPassword = await hashPassword(superAdminNewPassword);
      
      const { error } = await supabase
        .from('users')
        .update({ password: hashedPassword })
        .eq('email', SUPER_ADMIN_EMAIL);

      if (error) throw error;

      const plainPassword = superAdminNewPassword; // Save for display
      
      // Update local storage with new password hash
      const updatedUser = { ...user, password: hashedPassword };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      
      setShowSuperAdminPasswordModal(false);
      setSuperAdminNewPassword('');
      
      alert(`Your password has been changed successfully!\n\nNew Password: ${plainPassword}\n\nPlease save this password securely.`);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    } finally {
      setSuperAdminPasswordLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setFormLoading(true);
      setFormError('');

      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', selectedUser.id);

      if (error) throw error;

      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setFormError('Failed to delete user. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: '',
      is_active: user.is_active
    });
    setFormError('');
    setShowEditModal(true);
  };

  const openPasswordModal = (user: User) => {
    setUserForPassword(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const openCreateModal = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'staff',
      password: '',
      is_active: true
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setFormError('');
  };

  // Show login modal if not authenticated
  if (!user) {
    return (
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
      />
    );
  }

  // Check if user is super admin
  if (user.email !== SUPER_ADMIN_EMAIL) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <div className="text-center">
            <i className="ri-shield-cross-line text-4xl text-red-500 mb-4"></i>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">Only the super administrator can access user management.</p>
            <Button onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'staff': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSuperAdmin = (email: string) => email === SUPER_ADMIN_EMAIL;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-4">
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Dashboard
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                User Management
              </h1>
              <p className="text-gray-600">Manage staff accounts and permissions</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowSuperAdminPasswordModal(true)}
                variant="outline"
                className="whitespace-nowrap"
              >
                <i className="ri-lock-password-line mr-2"></i>
                Change My Password
              </Button>
              <Button
                onClick={openCreateModal}
                className="whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-2"></i>
                Create User
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-team-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-shield-user-line text-red-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Administrators</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-settings-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Managers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'manager').length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-green-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Staff</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'staff').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">All Users</h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-blue-600"></i>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {user.full_name}
                              {isSuperAdmin(user.email) && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <i className="ri-vip-crown-line mr-1"></i>
                                  Super Admin
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            onClick={() => openPasswordModal(user)}
                            variant="outline"
                            className="text-sm"
                          >
                            <i className="ri-lock-password-line mr-1"></i>
                            Change Password
                          </Button>
                          <Button
                            onClick={() => openEditModal(user)}
                            variant="outline"
                            className="text-sm"
                          >
                            <i className="ri-edit-line mr-1"></i>
                            Edit
                          </Button>
                          <Button
                            onClick={() => openDeleteModal(user)}
                            className="text-sm bg-red-600 hover:bg-red-700 text-white"
                            disabled={isSuperAdmin(user.email)}
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-12">
                  <i className="ri-team-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-500">No users found</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New User</h3>
                
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <Input
                    label="Full Name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="Enter full name"
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="Enter email address"
                  />
                  
                  <Select
                    label="Role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    options={[
                      { value: 'staff', label: 'Staff' },
                      { value: 'manager', label: 'Manager' },
                      { value: 'admin', label: 'Administrator' }
                    ]}
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="flex space-x-2">
                      <Input
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Enter password"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={generatePassword}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-2 cursor-pointer"
                    />
                    <label htmlFor="is_active" className="text-sm text-gray-700 cursor-pointer">
                      Active user
                    </label>
                  </div>

                  {formError && (
                    <div className="text-red-600 text-sm">{formError}</div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={formLoading}
                    >
                      Create User
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit User</h3>
                
                <form onSubmit={handleEditUser} className="space-y-4">
                  <Input
                    label="Full Name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="Enter full name"
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-100"
                  />
                  
                  <Select
                    label="Role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    options={[
                      { value: 'staff', label: 'Staff' },
                      { value: 'manager', label: 'Manager' },
                      { value: 'admin', label: 'Administrator' }
                    ]}
                    disabled={selectedUser?.email === SUPER_ADMIN_EMAIL}
                  />

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-2 cursor-pointer"
                    />
                    <label htmlFor="edit_is_active" className="text-sm text-gray-700 cursor-pointer">
                      Active user
                    </label>
                  </div>

                  {formError && (
                    <div className="text-red-600 text-sm">{formError}</div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={formLoading}
                    >
                      Update User
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Modal */}
        {showPasswordModal && userForPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Changing password for: <strong>{userForPassword.full_name}</strong>
                  </p>
                  <p className="text-sm text-gray-500">{userForPassword.email}</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <div className="flex space-x-2">
                      <Input
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder="Enter new password"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={generateNewPassword}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setUserForPassword(null);
                        setNewPassword('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleChangePassword}
                      loading={passwordLoading}
                      disabled={!newPassword}
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Super Admin Change Password Modal */}
        {showSuperAdminPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Your Password</h3>
                
                <div className="mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-3">
                    <i className="ri-vip-crown-line text-yellow-600 text-2xl"></i>
                  </div>
                  <p className="text-sm text-gray-600">
                    You are changing the password for the super administrator account.
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    <strong>Email:</strong> {SUPER_ADMIN_EMAIL}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <div className="flex space-x-2">
                      <Input
                        value={superAdminNewPassword}
                        onChange={(e) => setSuperAdminNewPassword(e.target.value)}
                        required
                        placeholder="Enter new password"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={generateSuperAdminPassword}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowSuperAdminPasswordModal(false);
                        setSuperAdminNewPassword('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleChangeSuperAdminPassword}
                      loading={superAdminPasswordLoading}
                      disabled={!superAdminNewPassword}
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Delete User</h2>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                    setFormError('');
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                  <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                </div>
                <p className="text-center text-gray-700 mb-2">
                  Are you sure you want to delete this user?
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="text-sm text-gray-600">
                    <strong>Name:</strong> {selectedUser.full_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Email:</strong> {selectedUser.email}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Role:</strong> {selectedUser.role}
                  </p>
                </div>
                <p className="text-center text-sm text-red-600 mt-4">
                  This action cannot be undone.
                </p>
              </div>

              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {formError}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                    setFormError('');
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  loading={formLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete User
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}